/**
 * DiscoverySequenceSettingsStyled - Material-UI styled version that matches app theme
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  ContentCopy as CloneIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import discoveryConfigService from '../services/DiscoveryConfigService';

// Sortable Topic Card Component
const SortableTopicCard = ({ 
  topic, 
  topicIndex, 
  topicConfig, 
  isEditing,
  onToggleEnabled,
  onRemove,
  onToggleItem 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: topic.topic_key,
    disabled: !isEditing 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: topic.is_enabled ? 'background.paper' : 'action.disabled',
        opacity: topic.is_enabled ? 1 : 0.6,
        border: isDragging ? 2 : 1,
        borderColor: isDragging ? 'primary.main' : 'divider'
      }}
    >
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing && (
            <IconButton
              size="small"
              {...attributes}
              {...listeners}
              sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
            >
              <DragIcon />
            </IconButton>
          )}
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {topicConfig?.name || topic.topic_key}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {topicConfig?.description}
            </Typography>
          </Box>
          
          {isEditing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                size="small"
                checked={topic.is_enabled}
                onChange={() => onToggleEnabled(topicIndex)}
              />
              <IconButton
                size="small"
                onClick={() => onRemove(topicIndex)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Topic Items */}
        {topic.items && topic.items.length > 0 && (
          <Box sx={{ mt: 1, pl: isEditing ? 5 : 0 }}>
            {topic.items.map((item, itemIndex) => {
              const itemConfig = topicConfig?.items?.[item.item_key];
              return (
                <Box
                  key={item.item_key}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    opacity: item.is_enabled ? 1 : 0.5,
                    borderBottom: itemIndex < topic.items.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {itemConfig?.name || item.item_key}
                    </Typography>
                    <Chip
                      label={itemConfig?.templateType || 'Unknown'}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 16 }}
                    />
                  </Box>
                  
                  {isEditing && (
                    <Switch
                      size="small"
                      checked={item.is_enabled}
                      onChange={() => onToggleItem(topicIndex, itemIndex)}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const DiscoverySequenceSettingsStyled = ({ userId, onClose }) => {
  const [userSequences, setUserSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTopics, setAvailableTopics] = useState({});
  const [categories, setCategories] = useState({});
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const [newSequenceName, setNewSequenceName] = useState('');
  const [newSequenceRoleType, setNewSequenceRoleType] = useState('TECHNICAL');
  
  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadData();
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading discovery sequence data for userId:', userId);
      
      // Load static configuration first (this should always work)
      const topics = discoveryConfigService.getAvailableTopics();
      const cats = discoveryConfigService.getCategories();
      
      console.log('✅ Static config loaded:', { topicCount: Object.keys(topics).length, categories: Object.keys(cats).length });
      
      setAvailableTopics(topics);
      setCategories(cats);
      
      // Try to load user sequences (might fail if backend not running)
      let sequences = [];
      try {
        sequences = await discoveryConfigService.getUserSequences(userId);
        console.log('✅ User sequences loaded:', sequences.length);
      } catch (apiError) {
        console.warn('⚠️ Failed to load user sequences from API:', apiError.message);
        console.log('📝 Creating default sequence instead...');
        
        // Create a default sequence for demonstration
        const defaultSequence = discoveryConfigService.getDefaultSequenceForRoleType('TECHNICAL');
        sequences = [{
          id: 'demo_sequence',
          name: 'Demo Technical Sequence',
          description: 'Demo sequence (backend not connected)',
          role_type: 'TECHNICAL',
          is_default: 1,
          topics: defaultSequence.topics
        }];
        
        setError(`Backend not connected: ${apiError.message}. Showing demo data.`);
      }
      
      setUserSequences(sequences);
      
      // Select first sequence by default
      if (sequences.length > 0 && !selectedSequence) {
        setSelectedSequence(sequences[0]);
      }
      
    } catch (err) {
      console.error('❌ Critical error loading data:', err);
      setError(`Critical error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSequence = async () => {
    try {
      await discoveryConfigService.createSequenceFromDefault(
        userId,
        newSequenceRoleType,
        newSequenceName
      );
      
      setShowCreateModal(false);
      setNewSequenceName('');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCloneSequence = async () => {
    try {
      await discoveryConfigService.cloneSequence(
        cloneSourceId,
        newSequenceName,
        userId
      );
      
      setShowCloneModal(false);
      setNewSequenceName('');
      setCloneSourceId(null);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveSequence = async () => {
    if (!selectedSequence) return;

    try {
      const { isValid, errors } = discoveryConfigService.validateSequence(selectedSequence);
      
      if (!isValid) {
        setError(errors.join(', '));
        return;
      }

      await discoveryConfigService.updateSequence(selectedSequence.id, {
        name: selectedSequence.name,
        description: selectedSequence.description,
        is_default: selectedSequence.is_default,
        topics: selectedSequence.topics
      });
      
      setIsEditing(false);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDragEnd = (event) => {
    if (!isEditing) return;

    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const newTopics = Array.from(selectedSequence.topics);
      const oldIndex = newTopics.findIndex(topic => topic.topic_key === active.id);
      const newIndex = newTopics.findIndex(topic => topic.topic_key === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedTopics = arrayMove(newTopics, oldIndex, newIndex);
        
        // Update order_index
        reorderedTopics.forEach((topic, index) => {
          topic.order_index = index;
        });

        setSelectedSequence({
          ...selectedSequence,
          topics: reorderedTopics
        });
      }
    }
  };

  const toggleTopicEnabled = (topicIndex) => {
    if (!isEditing || !selectedSequence) return;

    const newTopics = [...selectedSequence.topics];
    newTopics[topicIndex].is_enabled = !newTopics[topicIndex].is_enabled;

    setSelectedSequence({
      ...selectedSequence,
      topics: newTopics
    });
  };

  const removeTopicFromSequence = (topicIndex) => {
    if (!isEditing || !selectedSequence) return;

    const newTopics = selectedSequence.topics.filter((_, index) => index !== topicIndex);
    // Update order_index
    newTopics.forEach((topic, index) => {
      topic.order_index = index;
    });

    setSelectedSequence({
      ...selectedSequence,
      topics: newTopics
    });
  };

  const toggleItemEnabled = (topicIndex, itemIndex) => {
    if (!isEditing || !selectedSequence) return;

    const newTopics = [...selectedSequence.topics];
    newTopics[topicIndex].items[itemIndex].is_enabled = !newTopics[topicIndex].items[itemIndex].is_enabled;

    setSelectedSequence({
      ...selectedSequence,
      topics: newTopics
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading discovery sequences...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '80vh', width: '90vw', maxWidth: 1200 }}>
      {/* Header */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
        <IconButton onClick={onClose} color="inherit">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Left Panel - Sequences List */}
      <Paper sx={{ width: 320, mr: 2, p: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Your Sequences</Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={() => setShowCreateModal(true)}
          >
            Create
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {userSequences.map(sequence => (
            <Card
              key={sequence.id}
              sx={{
                mb: 1,
                cursor: 'pointer',
                border: selectedSequence?.id === sequence.id ? 2 : 1,
                borderColor: selectedSequence?.id === sequence.id ? 'primary.main' : 'divider'
              }}
              onClick={() => {
                setSelectedSequence(sequence);
                setIsEditing(false);
              }}
            >
              <CardContent sx={{ py: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {sequence.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {sequence.role_type}
                    </Typography>
                    {sequence.is_default === 1 && (
                      <Chip label="Default" size="small" color="primary" sx={{ ml: 1, height: 16 }} />
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCloneSourceId(sequence.id);
                        setShowCloneModal(true);
                      }}
                    >
                      <CloneIcon fontSize="small" />
                    </IconButton>
                    
                    {sequence.is_default !== 1 && (
                      <IconButton size="small">
                        <StarBorderIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Paper>

      {/* Right Panel - Sequence Editor */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {selectedSequence ? (
          <>
            {/* Editor Header */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{selectedSequence.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedSequence.role_type} • {selectedSequence.topics?.length || 0} topics
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {isEditing ? (
                    <>
                      <Button 
                        startIcon={<SaveIcon />} 
                        variant="contained" 
                        size="small"
                        onClick={handleSaveSequence}
                      >
                        Save
                      </Button>
                      <Button
                        startIcon={<CancelIcon />}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setIsEditing(false);
                          loadData(); // Reset changes
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      startIcon={<EditIcon />}
                      variant="contained"
                      size="small"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>

            {/* Topics Editor */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={selectedSequence.topics?.map(t => t.topic_key) || []}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedSequence.topics?.map((topic, topicIndex) => {
                    const topicConfig = availableTopics[topic.topic_key];
                    
                    return (
                      <SortableTopicCard
                        key={topic.topic_key}
                        topic={topic}
                        topicIndex={topicIndex}
                        topicConfig={topicConfig}
                        isEditing={isEditing}
                        onToggleEnabled={toggleTopicEnabled}
                        onRemove={removeTopicFromSequence}
                        onToggleItem={toggleItemEnabled}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="h6" color="text.secondary">
              Select a sequence to view and edit
            </Typography>
          </Box>
        )}
      </Box>

      {/* Create Sequence Dialog */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <DialogTitle>Create New Sequence</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Sequence Name"
            value={newSequenceName}
            onChange={(e) => setNewSequenceName(e.target.value)}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role Type</InputLabel>
            <Select
              value={newSequenceRoleType}
              onChange={(e) => setNewSequenceRoleType(e.target.value)}
            >
              {discoveryConfigService.getAvailableRoleTypes().map(roleType => (
                <MenuItem key={roleType} value={roleType}>{roleType}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateSequence}
            disabled={!newSequenceName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Sequence Dialog */}
      <Dialog open={showCloneModal} onClose={() => setShowCloneModal(false)}>
        <DialogTitle>Clone Sequence</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Sequence Name"
            value={newSequenceName}
            onChange={(e) => setNewSequenceName(e.target.value)}
            margin="normal"
            placeholder="Enter new sequence name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCloneModal(false);
            setCloneSourceId(null);
            setNewSequenceName('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCloneSequence}
            disabled={!newSequenceName.trim()}
          >
            Clone Sequence
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiscoverySequenceSettingsStyled;